import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Card, Grid, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import dayjs from 'dayjs';
import { useRetention } from '../RetentionContext';
import FromCrmCard from '../components/FromCrmCard';
import { EmptyState, IconBadge, InfoList, SectionCard, SoftChip, StatTile } from '../components/ui';
import { PHASES, STAGE_OWNER, WAITING_ON, stageByNo } from '../constants/stages';
import { RT } from '../theme/retentionTheme';
import { getHomeTasks, getRetentionTimeline } from '../../../redux/features/retentionSlice';

const WAITING_COLOR = { 1: RT.primary, 2: '#7C3AED', 3: '#0E7C86', 4: RT.muted };
const fmt = (d) => (d ? dayjs(d).format('DD MMM, HH:mm') : '—');
const toVerify = <SoftChip label="To verify" icon="solar:danger-circle-bold" color={RT.warning} />;

// Home tab: next action, quick stats, today's tasks for this student, details (§2.3).
export default function Home() {
  const { courseEnrollmentId, student, currentStage, progressByStage } = useRetention();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { homeTasks, timeline } = useSelector((state) => state.retention);
  const stage = stageByNo(currentStage);
  const phase = PHASES[stage?.phase || 1];

  // progressByStage changes after every stage action (refresh), so the tasks follow the stepper
  useEffect(() => {
    dispatch(getHomeTasks({ course_enrollment_id: courseEnrollmentId }));
    dispatch(getRetentionTimeline(courseEnrollmentId));
  }, [dispatch, courseEnrollmentId, progressByStage]);

  const tasks = Array.isArray(homeTasks.data) ? homeTasks.data : [];
  const recent = Array.isArray(timeline.data) ? timeline.data.slice(0, 5) : [];
  const openTask = (t) => navigate(`/student-journey/${courseEnrollmentId}/${stageByNo(t.stage_no)?.tab || 'onboarding'}?stage=${t.stage_no}`);
  // tutor asks for help, suggestions due, remap pending, call follow-up due (home-tasks warnings.flagged)
  const flagged = homeTasks?.meta?.warnings?.flagged || [];

  return (
    <Stack gap={2.5}>
      {/* next action */}
      <Card sx={{ background: `linear-gradient(100deg, ${phase.light} 0%, #FFFFFF 70%)`, borderColor: alpha(phase.color, 0.25) }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={2} sx={{ p: 2.5 }}>
          <IconBadge icon={stage?.icon} color={phase.color} size={56} solid sx={{ background: phase.gradient }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: phase.color }}>
              Next action · stage {stage?.no}
            </Typography>
            <Typography variant="h5">{stage?.short}</Typography>
            <Stack direction="row" gap={0.75} mt={0.75}>
              <SoftChip label={STAGE_OWNER[stage?.owner]} icon="solar:user-id-bold" color={phase.color} />
              <SoftChip label={phase.label} icon={phase.icon} color={phase.color} />
            </Stack>
          </Box>
          <Button
            size="large"
            variant="contained"
            endIcon={<Icon icon="solar:arrow-right-linear" width={18} />}
            onClick={() => navigate(`/student-journey/${courseEnrollmentId}/${stage?.tab || 'onboarding'}${stage ? `?stage=${stage.no}` : ''}`)}
            sx={{ background: phase.gradient }}
          >
            Open stage
          </Button>
        </Stack>
      </Card>

      {flagged.length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {flagged.map((f, i) => (
            <SoftChip key={i} label={f.label} icon="solar:flag-bold" color="#7C3AED" />
          ))}
        </Stack>
      )}

      {/* quick stats */}
      <Stack direction="row" gap={2} flexWrap="wrap">
        <StatTile
          icon="solar:layers-bold-duotone"
          color="#2563EB"
          label="Sessions done"
          value={`${student?.sessions_done ?? 0} / ${student?.total_session_count ?? '—'}`}
        />
        <StatTile
          icon="solar:check-square-bold-duotone"
          color={RT.success}
          label="Attendance"
          value={student?.attendance_percent != null ? `${student.attendance_percent}%` : '—'}
        />
        <StatTile icon="solar:phone-calling-bold-duotone" color="#7C3AED" label="Last contact" value={student?.last_contact_on} />
        <StatTile icon="solar:calendar-mark-bold-duotone" color={RT.warning} label="Renewal" value={student?.renewal_date} />
      </Stack>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <Stack gap={2.5}>
            <SectionCard
              icon="solar:checklist-minimalistic-bold-duotone"
              title="Today's tasks"
              action={tasks.length ? <SoftChip label={tasks.length} color={RT.primary} /> : null}
            >
              {tasks.length === 0 ? (
                <EmptyState
                  icon={homeTasks.loading ? 'svg-spinners:ring-resize' : 'solar:cup-first-bold-duotone'}
                  title={homeTasks.loading ? 'Loading…' : homeTasks.error ? 'Could not load tasks' : 'Nothing due'}
                  caption={homeTasks.loading ? '' : homeTasks.error?.message || ''}
                  compact
                />
              ) : (
                <Stack gap={1}>
                  {tasks.map((t, i) => {
                    const ts = stageByNo(t.stage_no);
                    const tp = PHASES[ts?.phase || 1];
                    return (
                      <Stack
                        key={i}
                        direction="row"
                        alignItems="center"
                        gap={1.5}
                        onClick={() => openTask(t)}
                        sx={{ p: 1.25, borderRadius: 2.5, border: `1px solid ${RT.line}`, cursor: 'pointer', '&:hover': { bgcolor: '#FAFBFD' } }}
                      >
                        <IconBadge icon={ts?.icon} color={tp.color} size={36} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" noWrap>
                            {t.pending_update}
                          </Typography>
                          <Typography variant="caption" sx={{ color: RT.muted }}>
                            Stage {t.stage_no} · {ts?.short}
                          </Typography>
                        </Box>
                        <SoftChip label={WAITING_ON[t.waiting_on] || '—'} color={WAITING_COLOR[t.waiting_on] || RT.muted} />
                        <Typography variant="caption" sx={{ fontWeight: 800, color: t.is_overdue ? RT.error : RT.ink2, minWidth: 64, textAlign: 'right' }}>
                          {t.is_overdue ? 'Overdue · ' : ''}
                          {fmt(t.due_at)}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </SectionCard>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <SectionCard icon="solar:user-rounded-bold-duotone" color="#2563EB" title="Student & parent" sx={{ height: '100%' }}>
                  <InfoList
                    rows={[
                      { icon: 'solar:square-academic-cap-linear', label: 'Class', value: student?.class },
                      { icon: 'solar:phone-linear', label: 'Parent mobile', value: student?.contactNumber },
                      { icon: 'ic:baseline-whatsapp', label: 'WhatsApp', value: student?.whatsapp_number || toVerify },
                      { icon: 'solar:globe-linear', label: 'Time zone', value: student?.time_zone || toVerify },
                    ]}
                  />
                </SectionCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <SectionCard icon="solar:book-bookmark-bold-duotone" color="#7C3AED" title="Enrolment" sx={{ height: '100%' }}>
                  <InfoList
                    rows={[
                      { icon: 'solar:book-linear', label: 'Course', value: student?.assigned_course_name },
                      { icon: 'solar:notebook-linear', label: 'Subjects', value: student?.subjects },
                      { icon: 'solar:calendar-linear', label: 'Preference', value: student?.session_preference_status },
                      { icon: 'solar:square-academic-cap-linear', label: 'Tutor', value: student?.tutor_name },
                    ]}
                  />
                </SectionCard>
              </Grid>
            </Grid>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack gap={2.5}>
            <FromCrmCard student={student} />
            <SectionCard
              icon="solar:history-bold-duotone"
              color={RT.muted}
              title="Recent activity"
              action={
                <Button size="small" onClick={() => navigate(`/student-journey/${courseEnrollmentId}/timeline`)}>
                  All
                </Button>
              }
            >
              {recent.length === 0 ? (
                <EmptyState
                  icon={timeline.loading ? 'svg-spinners:ring-resize' : 'solar:history-linear'}
                  title={timeline.loading ? 'Loading…' : 'No activity yet'}
                  compact
                />
              ) : (
                <Stack gap={1.25}>
                  {recent.map((ev, i) => (
                    <Box key={i} sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap title={ev.title}>
                        {ev.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: RT.muted }}>
                        {[fmt(ev.event_at), ev.actor_name].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
